import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { CertificateChain } from '../types/models/Certificate';
import type { ApiResponseCertificateChain } from '../types/api/ApiResponseCertificate';

interface UseCertificateChainReturn {
  chain: CertificateChain | null;
  loading: boolean;
  error: string | null;
  // HTTP status accompanying `error`, so the caller can branch per the
  // documented failure modes (404 unknown id, 409 not issued yet, 502
  // upstream failure). Only set when the response body parsed as JSend — a
  // reverse-proxy error page's status must not be mistaken for a documented
  // failure mode — and null for network-level failures that never produced a
  // response.
  errorStatus: number | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch a certificate's live full chain (GET /datacenters/certificates/{id}/chain):
 * the leaf plus its issuer chain in fullchain order, walked from the leaf's
 * ACTUAL issuer on the platform — so it is correct on every platform type,
 * including ACME platforms where the issuing intermediate varies per
 * certificate.
 *
 * This is a live call to the upstream platform and can take a couple of
 * seconds — only mount the consuming component lazily (`mountOnEnter` on the
 * parent Tabs), never fetch it eagerly with the page load. A previously
 * loaded chain stays available while a refetch is in flight.
 */
export const useCertificateChain = (certificateId: number): UseCertificateChainReturn => {
  const [chain, setChain] = useState<CertificateChain | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchChain = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorStatus(null);

      const response = await apiFetch(`/datacenters/certificates/${certificateId}/chain`);
      let body: ApiResponseCertificateChain | null;
      try {
        body = (await response.json()) as ApiResponseCertificateChain;
      } catch {
        body = null;
      }

      if (body?.status === 'success' && body.data?.chain) {
        setChain(body.data.chain);
        return;
      }

      setError(
        body?.message ??
          `Failed to load the certificate chain (HTTP ${`${response.status} ${response.statusText}`.trim()})`,
      );
      if (body !== null) {
        setErrorStatus(response.status);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the certificate chain');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, certificateId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchChain();
    }
  }, [auth.isAuthenticated, fetchChain]);

  return { chain, loading, error, errorStatus, refetch: fetchChain };
};
